/**
 * Variant Mapping tab — Inc-15 header + Variants management table (UC-172 / UC-173).
 *
 * Visual contract: artifacts/15/design-refs/figabl-eve/prototype/index.html screen 3a
 * Implements ACs: UC-172 AC1–AC7, UC-173 AC1–AC7
 * Plan: artifacts/15/build-plans/UC-172.md, artifacts/15/build-plans/UC-173.md
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useVariantMappingRows } from '@/hooks/useVariantMappingRows';
import { useVariantCatalog } from '@/hooks/useVariantCatalog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import UploadMappingsCsvModal from './UploadMappingsCsvModal';
import VariantsManagementTable from './VariantsManagementTable';
import ConflictResolutionDialog from './ConflictResolutionDialog';
import VariantMappingTableKey from './VariantMappingTableKey';
import { paginateVariantMappingRows } from './variantMappingTableUtils';
import './variantMappingDesign.css';
import {
  VARIANT_MANAGEMENT_DEFAULT_PAGE_SIZE,
  VARIANT_MANAGEMENT_PAGE_SIZES,
  buildVariantManagementColumns,
  buildVariantManagementRows,
  filterVariantManagementRows,
  resolveVariantManagementPageSize,
  variantManagementCellValue,
} from '@/shared/variantMapping/variantManagementColumns';
import { formatEveId } from '@/shared/variantMapping/formatEveId';
import { toMatchKey } from '@/shared/variantMapping/matchKey';
import {
  ASSOCIATE_SUCCESS_MESSAGE,
  DISASSOCIATE_LAST_IDENTIFIER_MESSAGE,
  associateVariantRows,
  disassociateVariantIdentifier,
  resolveAssociateIdentifier,
} from '@/shared/variantMapping/variantAssociation';
import { recalculateOpenPosForSkus } from '@/shared/variantMapping/recalculateOpenPoFunding';
import { supabase } from '@/integrations/supabase/client';
import { useOrganisationDefaultColumns } from '@/hooks/useOrganisationDefaultColumns';
import { resolveWarningOrder, warningRequeryKey } from '@/lib/designations/warningState';
import { RESET_BOTH_FAILURE, boughtAsOptions, selectBoughtAs, selectSoldAs, soldAsOptions, type SoldAsOption } from '@/lib/designations/rowOverrides';
import {
  designationPinsAfterAssociate,
  designationPinsAfterDisassociate,
  survivorIdentifiersAfterAssociate,
} from '@/lib/designations/associationDesignationPins';
import { applyVariantDesignationPins, resetVariantDesignations, setBoughtAsOverride, setSoldAsOverride } from '@/lib/designations/persistDesignationDefaults';
import type {
  VariantManagementIdentifier,
  VariantManagementRow,
} from '@/shared/types/variantMapping';

interface VariantMappingTabProps {
  organisationId: string;
}

const VariantMappingTab: React.FC<VariantMappingTabProps> = ({ organisationId }) => {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();

  const {
    data: rows = [],
    isLoading: rowsLoading,
    isError: rowsError,
    dataUpdatedAt: rowsUpdatedAt,
  } = useVariantMappingRows(organisationId);
  const {
    data: catalog = [],
    isLoading: catalogLoading,
    isError: catalogError,
    dataUpdatedAt: catalogUpdatedAt,
  } = useVariantCatalog(organisationId);

  const [searchQuery, setSearchQuery] = useState('');
  const [uploadCsvOpen, setUploadCsvOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(VARIANT_MANAGEMENT_DEFAULT_PAGE_SIZE);
  const [associateRow, setAssociateRow] = useState<VariantManagementRow | null>(null);
  const [associateError, setAssociateError] = useState<string | null>(null);
  const [associateSubmitting, setAssociateSubmitting] = useState(false);
  const [disassociateRowId, setDisassociateRowId] = useState<string | null>(null);
  const [soldAsRow, setSoldAsRow] = useState<VariantManagementRow | null>(null);
  const [boughtAsRow, setBoughtAsRow] = useState<VariantManagementRow | null>(null);
  const [designationPending, setDesignationPending] = useState(false);
  const designationWrite = useRef(false);

  const invalidateRows = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['variant-mapping-workbench', organisationId] });
    queryClient.invalidateQueries({ queryKey: ['variant-mapping-rows', organisationId] });
    queryClient.invalidateQueries({ queryKey: ['variant-catalog', organisationId] });
  }, [queryClient, organisationId]);

  const requireAdmin = useCallback((): boolean => {
    if (!isAdmin) {
      toast.error('This action requires the admin role.');
      return false;
    }
    return true;
  }, [isAdmin]);

  const runDesignationWrite = useCallback(async (write: () => Promise<void>) => {
    if (designationWrite.current) return;
    designationWrite.current = true;
    setDesignationPending(true);
    try {
      await write();
    } finally {
      designationWrite.current = false;
      setDesignationPending(false);
    }
  }, []);

  const managementRows = useMemo(
    () =>
      buildVariantManagementRows(catalog, rows, {
        identifiersUnavailable: rowsError,
      }),
    [catalog, rows, rowsError],
  );
  const identifierTotal = managementRows.reduce((sum, row) => sum + row.linkedIdentifierCount, 0);
  const variantRowTotal = managementRows.length;
  const searchedRows = useMemo(
    () => filterVariantManagementRows(managementRows, searchQuery),
    [managementRows, searchQuery],
  );
  const columns = useMemo(() => buildVariantManagementColumns(searchedRows), [searchedRows]);
  const stableColumns = useMemo(() => buildVariantManagementColumns(managementRows), [managementRows]);
  const defaults = useOrganisationDefaultColumns({
    organisationId,
    rows: managementRows,
    columns,
    stableColumns,
    isAdmin,
  });
  const warningOrder = useRef<string[] | null>(null);
  const warningQueryKey = useRef('');
  const warningOrgId = useRef(organisationId);
  if (warningOrgId.current !== organisationId) {
    warningOrgId.current = organisationId;
    warningOrder.current = null;
    warningQueryKey.current = '';
  }
  const warningRefreshKey = warningRequeryKey({
    searchQuery,
    page,
    pageSize,
    variantIds: searchedRows.map((row) => row.variantId),
    rowsUpdatedAt,
    catalogUpdatedAt,
  });
  const orderedRows = useMemo(() => {
    const annotated = searchedRows.map((row) => ({
      ...row,
      id: row.variantId,
      warning: defaults.warningByRow.get(row.variantId) ?? false,
    }));
    const requery = warningQueryKey.current !== warningRefreshKey;
    const next = resolveWarningOrder({
      ready: defaults.designationsReady,
      previousIds: warningOrder.current,
      rows: annotated,
      requery,
    });
    if (next.commit) {
      warningOrder.current = next.rows.map((row) => row.id);
      warningQueryKey.current = warningRefreshKey;
    }
    return next.rows;
  }, [defaults.designationsReady, defaults.warningByRow, searchedRows, warningRefreshKey]);
  const { pageRows, totalPages, currentPage } = paginateVariantMappingRows(orderedRows, page, pageSize);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, pageSize]);

  const countUnavailable = rowsError || catalogError || rowsLoading || catalogLoading;

  const closeAssociateDialog = useCallback(() => {
    setAssociateRow(null);
    setAssociateError(null);
    setAssociateSubmitting(false);
  }, []);

  const handleAssociateRequest = useCallback(
    (row: VariantManagementRow) => {
      if (!requireAdmin()) return;
      setAssociateError(null);
      setSoldAsRow(null);
      setBoughtAsRow(null);
      setAssociateRow(row);
    },
    [requireAdmin],
  );

  const handleAssociateConfirm = useCallback(
    async (identifierValue: string) => {
      if (!associateRow) return;
      setAssociateSubmitting(true);
      setAssociateError(null);
      try {
        const lookup = resolveAssociateIdentifier(
          managementRows,
          associateRow.variantId,
          identifierValue,
        );
        const survivors = managementRows.filter((candidate) => {
          if (candidate.variantId === associateRow.variantId) return false;
          return candidate.identifiers.some((item) => {
            if (toMatchKey(item.value) !== toMatchKey(identifierValue)) return false;
            if (!lookup?.identifierType) return true;
            return item.type === lookup.identifierType.trim().toLowerCase();
          });
        });
        const survivor = survivors.length === 1 ? survivors[0] : undefined;
        const survivorPins = survivor
          ? designationPinsAfterAssociate({
              survivorIdentifiers: survivor.identifiers.map((item) => ({ type: item.type, value: item.value })),
              mergedIdentifiers: survivorIdentifiersAfterAssociate({
                catalog,
                mappingRows: rows,
                sourceMasterProductId: associateRow.variantId,
                survivorMasterProductId: survivor.variantId,
              }),
              soldEffectiveKey: defaults.soldAsByRow[survivor.variantId]?.columnKey ?? null,
              boughtEffectiveKey: defaults.boughtAsByRow[survivor.variantId]?.columnKey ?? null,
            })
          : null;
        const result = await associateVariantRows(supabase, {
          organisationId,
          sourceMasterProductId: associateRow.variantId,
          identifierValue,
          identifierType: lookup?.identifierType ?? null,
        });
        if (!result.ok) {
          setAssociateError(result.message);
          return;
        }
        const survivorId = result.survivor_master_product_id;
        if (survivorPins && survivor && survivorId === survivor.variantId) {
          const pinned = await applyVariantDesignationPins(supabase, {
            organisationId,
            rows: [{ masterProductId: survivorId, ...survivorPins }],
          });
          if (!pinned.ok) toast.error(pinned.message || 'Sold as and Bought as were not kept.');
        }
        toast.success(result.message || ASSOCIATE_SUCCESS_MESSAGE);
        const groupedSkus = [
          ...associateRow.identifiers.map((item) => item.value),
          ...(survivor?.identifiers.map((item) => item.value) ?? []),
          identifierValue,
        ];
        void recalculateOpenPosForSkus(organisationId, groupedSkus);
        closeAssociateDialog();
        invalidateRows();
        await queryClient.invalidateQueries({ queryKey: ['variant-designation-overrides', organisationId] });
      } catch (error) {
        setAssociateError(error instanceof Error ? error.message : 'Association failed.');
      } finally {
        setAssociateSubmitting(false);
      }
    },
    [associateRow, catalog, closeAssociateDialog, defaults.boughtAsByRow, defaults.soldAsByRow, invalidateRows, managementRows, organisationId, queryClient, rows],
  );

  const handleEnterDisassociate = useCallback(
    (row: VariantManagementRow) => {
      if (!requireAdmin()) return;
      setDisassociateRowId(row.variantId);
    },
    [requireAdmin],
  );

  const handleExitDisassociate = useCallback(() => {
    setDisassociateRowId(null);
  }, []);

  const handleDisassociateIdentifier = useCallback(
    async (row: VariantManagementRow, identifier: VariantManagementIdentifier) => {
      if (!requireAdmin()) return;
      if (!identifier.linkId) {
        toast.error('Unable to disassociate that identifier.');
        return;
      }
      if (row.linkedIdentifierCount <= 1 || row.identifiers.length <= 1) {
        toast.error(DISASSOCIATE_LAST_IDENTIFIER_MESSAGE);
        return;
      }
      try {
        const designationPins = designationPinsAfterDisassociate({
          sourceIdentifiers: row.identifiers.map((item) => ({ type: item.type, value: item.value })),
          removed: { type: identifier.type, value: identifier.value },
          soldEffectiveKey: defaults.soldAsByRow[row.variantId]?.columnKey ?? null,
          boughtEffectiveKey: defaults.boughtAsByRow[row.variantId]?.columnKey ?? null,
        });
        const result = await disassociateVariantIdentifier(supabase, {
          organisationId,
          sourceMasterProductId: row.variantId,
          linkId: identifier.linkId,
        });
        if (!result.ok) {
          toast.error(result.message || DISASSOCIATE_LAST_IDENTIFIER_MESSAGE);
          return;
        }
        const pinRows = [
          { masterProductId: row.variantId, ...designationPins.source },
          ...(result.new_master_product_id
            ? [{ masterProductId: result.new_master_product_id, ...designationPins.moved }]
            : []),
        ];
        const pinned = await applyVariantDesignationPins(supabase, {
          organisationId,
          rows: pinRows,
        });
        if (!pinned.ok) toast.error(pinned.message || 'Sold as and Bought as were not kept.');
        toast.success(result.message || 'Identifier disassociated.');
        void recalculateOpenPosForSkus(
          organisationId,
          row.identifiers.map((item) => item.value),
        );
        invalidateRows();
        await queryClient.invalidateQueries({ queryKey: ['variant-designation-overrides', organisationId] });
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Disassociation failed.');
      }
    },
    [defaults.boughtAsByRow, defaults.soldAsByRow, invalidateRows, organisationId, queryClient, requireAdmin],
  );

  return (
    <div className="vm-design">
      <div
        data-testid="variant-mapping-controls"
        className="vm-design__controls"
      >
        <div data-testid="variant-mapping-controls-left" className="vm-design__controls-left">
          <div className="vm-design__search">
            <Search className="vm-design__search-icon" aria-hidden />
            <Input
              placeholder="Search Variant Mapping..."
              className="vm-design__search-input !h-11 !rounded-lg !border-[#d0d5dd] !bg-white !pl-[34px] !pr-[13px] !text-[14px] !shadow-none placeholder:!text-[#98a2b3] focus-visible:!ring-0"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Button
            type="button"
            variant="outline"
            className="vm-design__upload !h-11 !rounded-lg !border-[#e9eaee] !bg-[#f7f8fa] !px-5 !text-[15px] !font-bold !text-[#0f1222] hover:!bg-[#2f2c6b] hover:!text-white hover:!border-[#2f2c6b]"
            onClick={() => {
              if (!requireAdmin()) return;
              setUploadCsvOpen(true);
            }}
          >
            Upload mappings
          </Button>
          <VariantMappingTableKey />
        </div>
        <div data-testid="variant-mapping-controls-right" className="vm-design__controls-right">
          <p data-testid="variant-mapping-count" className="vm-design__stat" aria-live="polite">
            <span className="vm-design__stat-label">Variants</span>
            {' '}
            <span className="vm-design__stat-value">{countUnavailable ? '—' : identifierTotal}</span>
            {!countUnavailable && (
              <>
                {' '}
                <span className="vm-design__stat-sub">across {variantRowTotal} sets</span>
              </>
            )}
          </p>
          <label className="vm-design__page-size">
            <span>Rows per page</span>
            <select
              data-testid="page-size-select"
              aria-label="Rows per page"
              value={pageSize.toString()}
              onChange={(event) => setPageSize(resolveVariantManagementPageSize(event.target.value))}
              className="vm-design__page-size-select"
            >
              {VARIANT_MANAGEMENT_PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <VariantsManagementTable
        rows={catalogError ? [] : pageRows}
        columns={columns}
        isLoading={catalogLoading}
        currentPage={currentPage}
        totalPages={catalogError ? 1 : totalPages}
        onPageChange={setPage}
        onAssociateRequest={handleAssociateRequest}
        associateRow={associateRow}
        associateError={associateError}
        associateSubmitting={associateSubmitting}
        onAssociateCancel={closeAssociateDialog}
        onAssociateConfirm={handleAssociateConfirm}
        disassociateRowId={disassociateRowId}
        onEnterDisassociate={handleEnterDisassociate}
        onExitDisassociate={handleExitDisassociate}
        onDisassociateIdentifier={handleDisassociateIdentifier}
        organisationRowCount={managementRows.length}
        defaultEditing={defaults.editing}
        soldAsColumnKey={defaults.soldAsColumnKey}
        boughtAsColumnKey={defaults.boughtAsColumnKey}
        onConfigureDefaults={defaults.beginEditing}
        onSoldAsColumn={defaults.setSoldKey}
        onBoughtAsColumn={defaults.setBoughtKey}
        onCancelDefaults={defaults.cancelEditing}
        onApplyDefaults={defaults.requestApply}
        applyingDefaults={defaults.applying}
        onOverrideSoldAs={(row) => {
          if (!requireAdmin()) return;
          setAssociateRow(null);
          setBoughtAsRow(null);
          setSoldAsRow(row);
        }}
        onOverrideBoughtAs={(row) => {
          if (!requireAdmin()) return;
          setAssociateRow(null);
          setSoldAsRow(null);
          setBoughtAsRow(row);
        }}
        soldAsOverride={soldAsRow ? {
          rowId: soldAsRow.variantId,
          contextLabel: formatEveId(soldAsRow.eveId),
          options: soldAsOptions({
            columns,
            valuesByColumn: Object.fromEntries(columns.map((column) => [column.key, variantManagementCellValue(soldAsRow, column)])),
            effectiveColumnKey: defaults.soldAsByRow[soldAsRow.variantId]?.columnKey ?? null,
            effectiveMissing: defaults.soldAsByRow[soldAsRow.variantId]?.missing ?? true,
          }),
          resetEnabled: Boolean(defaults.soldAsByRow[soldAsRow.variantId]?.hasOverride),
          pending: designationPending,
          onDone: () => setSoldAsRow(null),
          onReset: () => {
            void runDesignationWrite(async () => {
              const result = await setSoldAsOverride(supabase, {
                organisationId,
                masterProductId: soldAsRow.variantId,
                columnKey: null,
                identifierType: null,
                action: 'reset',
              });
              if (!result.ok) {
                toast.error(result.message || 'Sold As was not changed.');
                return;
              }
              setSoldAsRow(null);
              await queryClient.invalidateQueries({ queryKey: ['variant-designation-overrides', organisationId] });
            });
          },
          onUseThis: (option: SoldAsOption) => {
            const defaultKey = defaults.soldAsColumnKey;
            const choice = selectSoldAs({
              option,
              defaultColumn: defaultKey
                ? { columnKey: defaultKey, identifierType: defaultKey.slice(0, defaultKey.indexOf(':')) || defaultKey }
                : null,
            });
            void runDesignationWrite(async () => {
              const result = await setSoldAsOverride(supabase, {
                organisationId,
                masterProductId: soldAsRow.variantId,
                columnKey: choice.override?.columnKey ?? null,
                identifierType: choice.override?.identifierType ?? null,
                action: choice.action,
              });
              if (!result.ok) {
                toast.error(result.message || 'Sold As was not changed.');
                return;
              }
              await queryClient.invalidateQueries({ queryKey: ['variant-designation-overrides', organisationId] });
            });
          },
        } : null}
        boughtAsOverride={boughtAsRow ? {
          rowId: boughtAsRow.variantId,
          contextLabel: formatEveId(boughtAsRow.eveId),
          options: boughtAsOptions({
            columns,
            valuesByColumn: Object.fromEntries(columns.map((column) => [column.key, variantManagementCellValue(boughtAsRow, column)])),
            effectiveColumnKey: defaults.boughtAsByRow[boughtAsRow.variantId]?.columnKey ?? null,
            effectiveMissing: defaults.boughtAsByRow[boughtAsRow.variantId]?.missing ?? true,
          }),
          resetEnabled: Boolean(defaults.boughtAsByRow[boughtAsRow.variantId]?.hasOverride),
          pending: designationPending,
          onDone: () => setBoughtAsRow(null),
          onReset: () => {
            void runDesignationWrite(async () => {
              const result = await setBoughtAsOverride(supabase, {
                organisationId,
                masterProductId: boughtAsRow.variantId,
                columnKey: null,
                identifierType: null,
                action: 'reset',
              });
              if (!result.ok) {
                toast.error(result.message || 'Bought As was not changed.');
                return;
              }
              setBoughtAsRow(null);
              await queryClient.invalidateQueries({ queryKey: ['variant-designation-overrides', organisationId] });
            });
          },
          onUseThis: (option: SoldAsOption) => {
            const defaultKey = defaults.boughtAsColumnKey;
            const choice = selectBoughtAs({
              option,
              defaultColumn: defaultKey
                ? { columnKey: defaultKey, identifierType: defaultKey.slice(0, defaultKey.indexOf(':')) || defaultKey }
                : null,
            });
            void runDesignationWrite(async () => {
              const result = await setBoughtAsOverride(supabase, {
                organisationId,
                masterProductId: boughtAsRow.variantId,
                columnKey: choice.override?.columnKey ?? null,
                identifierType: choice.override?.identifierType ?? null,
                action: choice.action,
              });
              if (!result.ok) {
                toast.error(result.message || 'Bought As was not changed.');
                return;
              }
              await queryClient.invalidateQueries({ queryKey: ['variant-designation-overrides', organisationId] });
            });
          },
        } : null}
        onResetDesignations={(row) => {
          if (!requireAdmin()) return;
          void runDesignationWrite(async () => {
            const result = await resetVariantDesignations(supabase, {
              organisationId,
              masterProductId: row.variantId,
            });
            if (!result.ok) {
              toast.error(result.message || RESET_BOTH_FAILURE);
              return;
            }
            await queryClient.invalidateQueries({ queryKey: ['variant-designation-overrides', organisationId] });
          });
        }}
        soldAsByRow={defaults.soldAsByRow}
        boughtAsByRow={defaults.boughtAsByRow}
      />

      <ConflictResolutionDialog
        open={defaults.conflict != null}
        copy={defaults.conflict}
        onCancel={defaults.clearConflict}
        onFollowDefaults={() => defaults.confirmPolicy('follow_defaults')}
        onOverrideAll={() => defaults.confirmPolicy('override_all')}
      />

      <UploadMappingsCsvModal
        open={uploadCsvOpen}
        organisationId={organisationId}
        onClose={() => setUploadCsvOpen(false)}
        onImported={invalidateRows}
      />
    </div>
  );
};

export default VariantMappingTab;
